// 움직이는 머리 소품 효과: 빙하(물방울·반짝임·김), 퍼핀(날갯짓), 미니 간헐천(분출). 프로토타입 itemFx 1179–1208.
// 라군의 큰 간헐천·밤 정도는 라군 방이 itemFxState에 넣어 준다.
import type * as THREE from 'three';
import type { Capy } from '../capy/agent';
import type { PoseContext } from '../capy/pose';

/** 라군이 매 프레임 채우는 값. gh: 큰 간헐천 높이(m, 최대 26), pre: 분출 전 부풀기 0~1, flapUntil: 퍼핀이 날갯짓을 멈출 시각. */
export const itemFxState = { gh: 0, pre: 0, flapUntil: 0 };

type Sprite = THREE.Sprite & { material: THREE.SpriteMaterial };
type BasicMesh = THREE.Mesh & { material: THREE.MeshBasicMaterial };

interface GlacierData { kind: 'glacier'; drip: THREE.Mesh; steam: Sprite[]; glints: Sprite[]; seed: number; wet?: number; nightK?: number }
interface PuffinData { kind: 'puffin'; wings: THREE.Group[]; seed: number; flap?: number }
interface GeyserData { kind: 'minigeyser'; jet: BasicMesh; sheath: BasicMesh; dome: THREE.Mesh; drops: THREE.Mesh[]; dropMat: THREE.MeshBasicMaterial; steam: Sprite[]; seed: number }
type ItemData = GlacierData | PuffinData | GeyserData | { kind: 'auroracloud'; seed: number };

export function itemFx(a: Capy, t: number, dt: number, ctx: PoseContext): void {
  const it = a.item;
  if (!it || !it.userData.kind) return;
  const u = it.userData as ItemData, room = ctx.room;
  if (u.kind === 'glacier') {
    const wet = room === 'lagoon' && a.bathAmt > 0.5 ? 1 : 0;
    u.wet = (u.wet || 0) + (wet - (u.wet || 0)) * Math.min(1, dt * 1.5);
    const ph = (t * (0.45 + u.wet * 0.9) + u.seed) % 1;
    u.drip.position.set(0.1, 0.06 - ph * 0.55, 0.11); u.drip.visible = ph < 0.85;
    u.glints.forEach((sp, i) => { const q = (t * 0.8 + i * 0.37 + u.seed) % 1.6; const k2 = q < 0.25 ? Math.sin(q / 0.25 * Math.PI) : 0; sp.material.opacity = k2 * (0.9 + 0.4 * (1 - (u.nightK || 0))); sp.scale.setScalar(0.06 + 0.1 * k2); });
    u.steam.forEach((sp, i) => { const q = (t * 0.35 + i * 0.5 + u.seed) % 1; sp.position.set(Math.sin(i * 2 + t * 0.5) * 0.08, 0.45 + q * 0.5, 0); const sc = 0.25 + q * 0.45; sp.scale.set(sc, sc, 1); sp.material.opacity = u.wet! * 0.5 * Math.sin(q * Math.PI); });
  } else if (u.kind === 'puffin') {
    u.flap = (u.flap || 0) + ((t < itemFxState.flapUntil ? 1 : 0) - (u.flap || 0)) * Math.min(1, dt * 6);
    const w = Math.sin(t * 28 + u.seed) * 0.9 * u.flap;
    u.wings[0]!.rotation.z = 0.12 + Math.max(0, w) * 1.2; u.wings[1]!.rotation.z = -0.12 - Math.max(0, w) * 1.2;
  } else if (u.kind === 'minigeyser') {
    const inLag = room === 'lagoon', h = inLag ? itemFxState.gh / 26 : 0, pre = inLag ? itemFxState.pre : 0;
    // 분출 전 부글부글 부푸는 돔
    const dm = h > 0.02 ? 0.01 : pre * (0.85 + 0.15 * Math.sin(t * 16 + u.seed));
    u.dome.scale.set(Math.max(0.01, dm), Math.max(0.01, dm * (0.7 + 0.3 * Math.sin(t * 12))), Math.max(0.01, dm));
    // 물기둥: 밝은 심과 부드러운 바깥, 깜빡임
    const jh = h * 0.85 * (1 + 0.07 * Math.sin(t * 23 + u.seed) + 0.05 * Math.sin(t * 37));
    u.jet.visible = u.sheath.visible = jh > 0.01;
    u.jet.scale.set(0.7 + jh * 0.2, Math.max(0.01, jh), 0.7 + jh * 0.2); u.jet.material.opacity = Math.min(0.95, h * 4);
    u.sheath.scale.set(1.5 + jh * 0.6, Math.max(0.01, jh * 0.85), 1.5 + jh * 0.6); u.sheath.material.opacity = Math.min(0.4, h * 2);
    // 꼭대기에서 튀어 나가 포물선으로 떨어지는 물방울
    u.dropMat.opacity = Math.min(0.95, h * 5);
    u.drops.forEach((d, k) => {
      d.visible = h > 0.02;
      if (!d.visible) return;
      const q = (t * 1.2 + k / u.drops.length) % 1, ang = k * 2.4 + u.seed, sp = 0.12 + 0.18 * h, top = 0.22 + jh * 0.92;
      d.position.set(Math.cos(ang) * sp * q, top + (0.25 * h + 0.06) * q * 2 - 0.9 * q * q * (0.4 + h), Math.sin(ang) * sp * q); d.scale.setScalar(1 - q * 0.5);
    });
    // 김: 평소엔 가는 한 줄기, 분출할 땐 크게 피어오름
    u.steam.forEach((sp, k) => {
      const q = (t * 0.4 + k / 3 + u.seed) % 1;
      sp.position.set(Math.sin(k * 2.1 + t * 0.6) * 0.06 * (1 + h * 2), 0.3 + jh * 0.9 + q * (0.3 + h * 0.6), Math.cos(k * 1.7) * 0.05);
      const sc = 0.25 + q * 0.45 + h * 0.9; sp.scale.set(sc, sc, 1); sp.material.opacity = (0.18 + 0.6 * h + 0.2 * pre) * Math.sin(q * Math.PI);
    });
  }
}
